import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Search, Filter, Eye, X, CheckCircle, XCircle, Clock, AlertCircle, ExternalLink, Link2, BookOpen, Trash2, CheckSquare, Square, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import axios from 'axios';
import './History.css';

const History = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedProject, setSelectedProject] = useState(() => {
    const saved = localStorage.getItem('selectedProject');
    return (saved === 'unassigned') ? 'all' : (saved || 'all');
  });
  const [selectedEngine, setSelectedEngine] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Серверная пагинация
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [stats, setStats] = useState({ total: 0, completed: 0, errors: 0 });

  // Debounce для поиска
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Сброс на первую страницу при изменении фильтров
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProject, selectedEngine, debouncedSearchQuery, dateFrom, dateTo]);

  // Загрузка истории проверок с серверной пагинацией
  const fetchHistory = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }

      const response = await axios.get('/api/check-history', {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          project_id: selectedProject,
          search_engine: selectedEngine,
          search: debouncedSearchQuery || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined
        }
      });

      // Новый формат ответа с пагинацией
      if (response.data && response.data.data) {
        setHistory(response.data.data);
        setTotalItems(response.data.pagination.total);
        setTotalPages(response.data.pagination.totalPages);
        setStats(response.data.stats);
      } else if (response.data && Array.isArray(response.data)) {
        // Обратная совместимость со старым форматом
        setHistory(response.data);
        setTotalItems(response.data.length);
        setTotalPages(Math.ceil(response.data.length / itemsPerPage));
      } else {
        console.error('Invalid history data format:', response.data);
        setHistory([]);
        setTotalItems(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      if (error.response?.status === 401) {
        console.error('Unauthorized - token may have expired');
      }
    } finally {
      if (!silent) {
        setLoading(false);
        setIsInitialLoad(false);
      }
    }
  }, [currentPage, itemsPerPage, selectedProject, selectedEngine, debouncedSearchQuery, dateFrom, dateTo]);

  // Загрузка проектов
  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get('/api/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, []);

  // Начальная загрузка данных
  useEffect(() => {
    if (user) {
      fetchHistory();
      fetchProjects();
    }
  }, [user, fetchHistory, fetchProjects]);

  // Отдельный useEffect для автообновления
  useEffect(() => {
    if (!user) return;

    // Проверяем есть ли активные задачи (running или pending)
    const hasActiveTasks = history.some(check =>
      check.status === 'running' || check.status === 'pending'
    );

    // Если есть активные задачи - обновляем каждые 2 секунды, иначе каждые 30 секунд
    const intervalTime = hasActiveTasks ? 2000 : 30000;

    const interval = setInterval(() => {
      fetchHistory(true);
    }, intervalTime);

    return () => clearInterval(interval);
  }, [user, fetchHistory, history]);

  // Обновление при возврате фокуса на страницу
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchHistory(true);
      }
    };

    const handleFocus = () => {
      fetchHistory(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user, fetchHistory]);

  // Клиентская фильтрация больше не нужна - всё на сервере

  // Парсинг данных проверки
  const parseCheckData = useCallback((check) => {
    const defaultData = {
      serpItems: [],
      sources: [],
      searchQueries: [],
      hints: [],
      isAnswerRejected: false,
      isBulletAnswer: false,
      problematicAnswer: false
    };

    if (!check || !check.serp_items) {
      return defaultData;
    }

    try {
      let parsed = null;

      // Проверяем, это строка или объект
      if (typeof check.serp_items === 'string') {
        const trimmed = check.serp_items.trim();
        // Парсим JSON-строку, если не пустая
        if (trimmed !== '' && trimmed !== 'null' && trimmed !== 'undefined') {
          try {
            parsed = JSON.parse(trimmed);
          } catch (jsonError) {
            console.error('[parseCheckData] JSON parse error:', jsonError.message);
            return defaultData;
          }
        }
      } else if (typeof check.serp_items === 'object' && check.serp_items !== null) {
        // Уже объект
        parsed = check.serp_items;
      }

      if (!parsed) {
        return defaultData;
      }

      // Проверяем, это новый формат (объект) или старый (массив)
      if (Array.isArray(parsed)) {
        return { ...defaultData, serpItems: parsed };
      } else if (typeof parsed === 'object') {
        // Новый формат - объект с полями
        return {
          serpItems: Array.isArray(parsed.serpItems) ? parsed.serpItems : [],
          sources: Array.isArray(parsed.sources) ? parsed.sources : [],
          searchQueries: Array.isArray(parsed.searchQueries) ? parsed.searchQueries : [],
          hints: Array.isArray(parsed.hints) ? parsed.hints : [],
          isAnswerRejected: Boolean(parsed.isAnswerRejected),
          isBulletAnswer: Boolean(parsed.isBulletAnswer),
          problematicAnswer: Boolean(parsed.problematicAnswer)
        };
      }
    } catch (e) {
      console.error('[parseCheckData] Unexpected error:', e.message);
    }

    return defaultData;
  }, []);

  // Получение иконки статуса
  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={18} className="status-icon success" />;
      case 'error':
        return <XCircle size={18} className="status-icon error" />;
      case 'running':
        return <Clock size={18} className="status-icon running" />;
      case 'pending':
        return <AlertCircle size={18} className="status-icon pending" />;
      default:
        return null;
    }
  }, []);

  // Получение текста статуса
  const getStatusText = useCallback((status) => {
    switch (status) {
      case 'completed':
        return 'Завершено';
      case 'error':
        return 'Ошибка';
      case 'running':
        return 'Выполняется';
      case 'pending':
        return 'В очереди';
      default:
        return status;
    }
  }, []);

  // Форматирование даты
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Показать детали проверки
  const showCheckDetails = useCallback(async (check) => {
    try {
      // Загружаем свежие данные проверки с сервера
      const response = await axios.get(`/api/check-history/${check.id}`);
      const freshCheck = response.data;

      setSelectedCheck(freshCheck);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error loading check details:', error);
      // Если не удалось загрузить свежие данные, используем текущие
      setSelectedCheck(check);
      setShowDetailModal(true);
    }
  }, []);

  // Удалить запись из истории
  const deleteCheck = useCallback(async (checkId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту запись?')) {
      return;
    }

    try {
      await axios.delete(`/api/check-history/${checkId}`);
      // Перезагружаем данные с сервера для корректной пагинации
      fetchHistory();
    } catch (error) {
      console.error('Error deleting check:', error);
      alert('Ошибка при удалении записи');
    }
  }, [fetchHistory]);

  // Функции для массового удаления
  const handleToggleSelect = useCallback((id) => {
    setSelectedForDelete(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      return newSelected;
    });
  }, []);

  const handleSelectAllForDelete = useCallback(() => {
    if (selectedForDelete.size === history.length) {
      setSelectedForDelete(new Set());
    } else {
      setSelectedForDelete(new Set(history.map(h => h.id)));
    }
  }, [history, selectedForDelete.size]);

  const handleBulkDelete = useCallback(() => {
    if (selectedForDelete.size === 0) return;
    setShowBulkDeleteConfirm(true);
  }, [selectedForDelete.size]);

  const confirmBulkDelete = useCallback(async () => {
    if (selectedForDelete.size === 0) return;

    try {
      setIsBulkDeleting(true);
      await axios.post('/api/check-history/bulk-delete', {
        ids: Array.from(selectedForDelete)
      });
      // Перезагружаем данные с сервера
      setSelectedForDelete(new Set());
      setShowBulkDeleteConfirm(false);
      fetchHistory();
    } catch (error) {
      console.error('Error bulk deleting checks:', error);
      alert('Ошибка при удалении записей');
    } finally {
      setIsBulkDeleting(false);
    }
  }, [selectedForDelete, fetchHistory]);

  const cancelBulkDelete = useCallback(() => {
    setShowBulkDeleteConfirm(false);
  }, []);

  // Индекс первого и последнего элемента на текущей странице (для отображения)
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Обработка смены страницы
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  // Обработка смены количества элементов на странице
  const handleItemsPerPageChange = useCallback((event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1); // Сбрасываем на первую страницу при смене количества элементов
  }, []);

  // Функция для изменения выбранного проекта с сохранением в localStorage
  const handleProjectChange = useCallback((projectId) => {
    setSelectedProject(projectId);
    localStorage.setItem('selectedProject', projectId);
  }, []);


  return (
    <div className="history">
      <div className="history-header">
        <h1>История проверок</h1>
        <div className="header-stats">
          <div className="stat-item">
            <span className="stat-label">Всего проверок:</span>
            <span className="stat-value">{stats.total}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Завершено:</span>
            <span className="stat-value success">
              {stats.completed}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Ошибок:</span>
            <span className="stat-value error">
              {stats.errors}
            </span>
          </div>
        </div>
      </div>

      <div className="history-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Поиск по запросу..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <Filter size={18} />
          <select
            value={selectedProject}
            onChange={(e) => handleProjectChange(e.target.value)}
            className="filter-select"
          >
            <option value="all">Все проекты</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.title}
              </option>
            ))}
          </select>

          <select
            value={selectedEngine}
            onChange={(e) => setSelectedEngine(e.target.value)}
            className="filter-select"
          >
            <option value="all">Все поисковики</option>
            <option value="google">Google</option>
            <option value="yandex">Yandex</option>
          </select>
        </div>

        <div className="date-filter-group">
          <label className="date-filter-label">
            <span>С:</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="date-input"
            />
          </label>
          <label className="date-filter-label">
            <span>По:</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="date-input"
            />
          </label>
          {(dateFrom || dateTo) && (
            <button
              className="clear-dates-btn"
              onClick={() => {
                setDateFrom('');
                setDateTo('');
              }}
              title="Сбросить даты"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="history-content">
        {loading ? (
          <div className="loading">Загрузка...</div>
        ) : totalItems === 0 ? (
          <div className="empty-state">
            <p>История проверок пуста</p>
            <p>Выполните проверку запросов на странице "Запросы"</p>
          </div>
        ) : (
          <div className="history-table-container">
            {/* Панель массового удаления */}
            {history.length > 0 && (
              <div className="bulk-actions-bar">
                <button
                  className="select-all-btn"
                  onClick={handleSelectAllForDelete}
                >
                  {selectedForDelete.size === history.length ? (
                    <><CheckSquare size={16} /> Снять выделение</>
                  ) : (
                    <><Square size={16} /> Выбрать все на странице</>
                  )}
                </button>
                {selectedForDelete.size > 0 && (
                  <button
                    className="bulk-delete-btn"
                    onClick={handleBulkDelete}
                  >
                    <Trash2 size={16} />
                    Удалить выбранные ({selectedForDelete.size})
                  </button>
                )}
              </div>
            )}
            <table className="history-table">
              <thead>
                <tr>
                  <th className="checkbox-cell">
                    <button
                      className="checkbox-btn"
                      onClick={handleSelectAllForDelete}
                      title={selectedForDelete.size === history.length ? 'Снять выделение' : 'Выбрать все'}
                    >
                      {selectedForDelete.size === history.length && history.length > 0 ? (
                        <CheckSquare size={18} />
                      ) : (
                        <Square size={18} />
                      )}
                    </button>
                  </th>
                  <th>№</th>
                  <th>Дата</th>
                  <th>Запрос</th>
                  <th>Проект</th>
                  <th>Регион</th>
                  <th>Поисковик</th>
                  <th>Вхождение</th>
                  <th>Сайт</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {history.map((check, index) => (
                  <tr key={check.id} className={`status-${check.status}${selectedForDelete.has(check.id) ? ' selected' : ''}`}>
                    <td className="checkbox-cell">
                      <button
                        className="checkbox-btn"
                        onClick={() => handleToggleSelect(check.id)}
                      >
                        {selectedForDelete.has(check.id) ? (
                          <CheckSquare size={18} />
                        ) : (
                          <Square size={18} />
                        )}
                      </button>
                    </td>
                    <td data-label="№">{indexOfFirstItem + index + 1}</td>
                    <td className="date-cell" data-label="Дата">{formatDate(check.check_date)}</td>
                    <td className="query-cell" data-label="Запрос">
                      <div className="query-text" title={check.query}>
                        {check.query}
                      </div>
                    </td>
                    <td className="project-cell" data-label="Проект">
                      {check.project_title || '—'}
                    </td>
                    <td data-label="Регион">{check.region}</td>
                    <td data-label="Поисковик">
                      <span className={`engine-badge ${check.search_engine}`}>
                        {check.search_engine === 'google' ? 'Google' : 'Yandex'}
                      </span>
                    </td>
                    <td className="brand-score-cell" data-label="Вхождение">
                      {check.status !== 'completed' ? (
                        <span className="brand-score-dash">—</span>
                      ) : check.brand_score === null || check.brand_score === undefined ? (
                        <span className="brand-score-dash">—</span>
                      ) : check.brand_score === 0 ? (
                        <span className="brand-score-zero">✗ Нет</span>
                      ) : (
                        <span className="brand-score high">✓ Да</span>
                      )}
                    </td>
                    <td className="site-mention-cell" data-label="Сайт">
                      {check.status !== 'completed' ? (
                        <span className="brand-score-dash">—</span>
                      ) : check.site_mentioned === null || check.site_mentioned === undefined ? (
                        <span className="brand-score-dash">—</span>
                      ) : check.site_mentioned ? (
                        <span className="site-mention-yes" title={(() => {
                          try {
                            const d = typeof check.site_mention_details === 'string' ? JSON.parse(check.site_mention_details) : check.site_mention_details;
                            const parts = [];
                            if (d?.inResponse) parts.push('в ответе');
                            if (d?.inSources) parts.push('в источниках');
                            if (d?.inSerp) parts.push('в SERP');
                            return parts.join(', ');
                          } catch { return ''; }
                        })()}>✓</span>
                      ) : (
                        <span className="site-mention-no">✗</span>
                      )}
                    </td>
                    <td className="status-cell" data-label="Статус">
                      <div className="status-badge" title={getStatusText(check.status)}>
                        {getStatusIcon(check.status)}
                      </div>
                    </td>
                    <td className="actions-cell" data-label="">
                      <button
                        className="action-btn view-btn"
                        onClick={() => showCheckDetails(check)}
                        title="Посмотреть детали"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => deleteCheck(check.id)}
                        title="Удалить запись"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Пагинация */}
            {totalItems > 0 && (
              <div className="pagination">
                <div className="pagination-info">
                  <span>Показано {indexOfFirstItem + 1}–{indexOfLastItem} из {totalItems}</span>
                </div>
                <div className="pagination-controls">
                  <button
                    className="btn-pagination"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    title="Первая страница"
                  >
                    <ChevronsLeft size={16} />
                  </button>
                  <button
                    className="btn-pagination"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    title="Предыдущая страница"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className="pagination-pages">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        // Показываем: первую, последнюю, текущую и соседние страницы
                        return page === 1 ||
                          page === totalPages ||
                          Math.abs(page - currentPage) <= 1;
                      })
                      .reduce((acc, page, idx, arr) => {
                        // Добавляем многоточие между разрывами
                        if (idx > 0 && page - arr[idx - 1] > 1) {
                          acc.push('...');
                        }
                        acc.push(page);
                        return acc;
                      }, [])
                      .map((item, idx) => (
                        item === '...' ? (
                          <span key={`ellipsis-${idx}`} className="pagination-ellipsis">...</span>
                        ) : (
                          <button
                            key={item}
                            className={`btn-pagination-page ${currentPage === item ? 'active' : ''}`}
                            onClick={() => handlePageChange(item)}
                          >
                            {item}
                          </button>
                        )
                      ))
                    }
                  </div>

                  <button
                    className="btn-pagination"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    title="Следующая страница"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <button
                    className="btn-pagination"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage >= totalPages}
                    title="Последняя страница"
                  >
                    <ChevronsRight size={16} />
                  </button>
                </div>

                {/* Выбор количества элементов на странице */}
                <div className="items-per-page">
                  <span>На странице:</span>
                  <select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="items-per-page-select"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Модальное окно с деталями проверки */}
      {showDetailModal && selectedCheck && (
        <DetailModal
          check={selectedCheck}
          onClose={() => setShowDetailModal(false)}
          parseCheckData={parseCheckData}
          getStatusIcon={getStatusIcon}
          getStatusText={getStatusText}
          formatDate={formatDate}
        />
      )}

      {/* Модальное окно подтверждения массового удаления */}
      {showBulkDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal confirm-modal">
            <div className="modal-header">
              <h2>Подтверждение удаления</h2>
              <button
                className="close-btn"
                onClick={cancelBulkDelete}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <p>Вы уверены, что хотите удалить выбранные записи ({selectedForDelete.size} шт.)?</p>
              <p className="warning-text">Это действие нельзя будет отменить.</p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelBulkDelete}
                disabled={isBulkDeleting}
              >
                Отмена
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmBulkDelete}
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Компонент модального окна вынесен отдельно для оптимизации
const DetailModal = ({ check, onClose, parseCheckData, getStatusIcon, getStatusText, formatDate }) => {
  const parsedData = useMemo(() => parseCheckData(check), [check]);

  return (
    <div className="modal-overlay">
      <div className="modal detail-modal">
        <div className="modal-header">
          <h2>Детали проверки</h2>
          <button
            className="close-btn"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {/* Основная информация */}
          <div className="detail-section">
            <h3>Основная информация</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Запрос:</span>
                <span className="detail-value">{check.query}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Проект:</span>
                <span className="detail-value">{check.project_title || '—'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Регион:</span>
                <span className="detail-value">{check.region}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Поисковик:</span>
                <span className="detail-value">
                  {check.search_engine === 'google' ? 'Google' : 'Yandex'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Дата проверки:</span>
                <span className="detail-value">{formatDate(check.check_date)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Статус:</span>
                <span className="detail-value">
                  <div className="status-badge">
                    {getStatusIcon(check.status)}
                    <span>{getStatusText(check.status)}</span>
                  </div>
                </span>
              </div>
              {/* Вхождение бренда */}
              <div className="detail-item">
                <span className="detail-label">Вхождение бренда:</span>
                <span className="detail-value">
                  {check.brand_score === null || check.brand_score === undefined ? (
                    <span className="brand-score-dash">Не проверено</span>
                  ) : check.brand_score === 0 ? (
                    <span className="brand-score-zero">✗ Не найден</span>
                  ) : (
                    <span className="brand-score-badge high">✓ Найден</span>
                  )}
                </span>
              </div>
              {/* Сайт в выдаче */}
              <div className="detail-item">
                <span className="detail-label">Сайт в выдаче:</span>
                <span className="detail-value">
                  {check.site_mentioned === null || check.site_mentioned === undefined ? (
                    <span className="brand-score-dash">Не проверено</span>
                  ) : check.site_mentioned ? (
                    <span className="site-mention-found">✓ Найден</span>
                  ) : (
                    <span className="site-mention-not-found">✗ Не найден</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Детали нахождения сайта */}
          {check.site_mentioned === 1 && check.site_mention_details && (
            <div className="detail-section site-mention-section">
              <h3>
                <ExternalLink size={20} />
                Сайт в выдаче — детали
              </h3>
              {(() => {
                try {
                  const details = typeof check.site_mention_details === 'string'
                    ? JSON.parse(check.site_mention_details)
                    : check.site_mention_details;
                  return (
                    <div className="site-mention-details">
                      <div className="site-mention-badges">
                        {details.inResponse && <span className="site-badge in-response">В ответе AI</span>}
                        {details.inSources && <span className="site-badge in-sources">В источниках</span>}
                        {details.inSerp && <span className="site-badge in-serp">В SERP</span>}
                      </div>
                      {details.matchedDomains?.length > 0 && (
                        <div className="site-mention-info">
                          <strong>Найденные домены:</strong> {details.matchedDomains.join(', ')}
                        </div>
                      )}
                      {details.serpPositions?.length > 0 && (
                        <div className="site-mention-info">
                          <strong>Позиции в SERP:</strong> {details.serpPositions.join(', ')}
                        </div>
                      )}
                      {details.sourceUrls?.length > 0 && (
                        <div className="site-mention-info">
                          <strong>URL в источниках:</strong>
                          <ul className="site-mention-urls">
                            {details.sourceUrls.map((url, i) => (
                              <li key={i}><a href={url} target="_blank" rel="noopener noreferrer">{url}</a></li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                } catch {
                  return null;
                }
              })()}
            </div>
          )}

          {/* Детали проверки бренда */}
          {check.status === 'completed' && check.ai_comment && (
            <div className="detail-section ai-analysis-section">
              <h3>
                <AlertCircle size={20} />
                Детали проверки бренда
              </h3>
              <div className="ai-comment">
                <p>{check.ai_comment}</p>
              </div>
            </div>
          )}

          {/* Ошибка */}
          {check.error_message && (
            <div className="detail-section error-section">
              <h3>Ошибка</h3>
              <div className="error-message">
                {/* Определяем тип ошибки и выводим подробности */}
                {check.error_message.includes('Gemini') && (
                  <div className="error-type">🤖 Ошибка Gemini AI:</div>
                )}
                {check.error_message.includes('SOAX') && (
                  <div className="error-type">🌐 Ошибка SOAX Proxy:</div>
                )}
                {check.error_message.includes('proxy') && !check.error_message.includes('SOAX') && (
                  <div className="error-type">🌐 Ошибка прокси:</div>
                )}
                {check.error_message.includes('Timeout') && (
                  <div className="error-type">⏱️ Таймаут:</div>
                )}
                {check.error_message.includes('rate limit') && (
                  <div className="error-type">⚠️ Превышен лимит запросов:</div>
                )}
                {check.error_message.includes('API key') && (
                  <div className="error-type">🔑 Ошибка API ключа:</div>
                )}
                <div className="error-details">{check.error_message}</div>
              </div>
            </div>
          )}

          {/* Метаданные ответа - показываем в начале */}
          {(parsedData.isAnswerRejected || parsedData.isBulletAnswer || parsedData.problematicAnswer) && (
            <div className="detail-section">
              <h3>
                <AlertCircle size={20} />
                Метаданные ответа
              </h3>
              <div className="metadata-grid">
                {parsedData.isAnswerRejected && (
                  <div className="metadata-item warning">
                    <XCircle size={18} />
                    <span>Ответ отклонён по этическим причинам</span>
                  </div>
                )}
                {parsedData.isBulletAnswer && (
                  <div className="metadata-item info">
                    <CheckCircle size={18} />
                    <span>Ответ в формате списка</span>
                  </div>
                )}
                {parsedData.problematicAnswer && (
                  <div className="metadata-item warning">
                    <AlertCircle size={18} />
                    <span>Проблемный ответ</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Ответ AI (Алиса для Yandex, AI Overview для Google) */}
          {check.alice_response && (
            <div className="detail-section">
              <h3>
                <BookOpen size={20} />
                {check.search_engine === 'google' ? 'AI Overview (Google)' : 'Ответ Алисы'}
              </h3>
              <div
                className="alice-response markdown-content"
                dangerouslySetInnerHTML={{ __html: check.alice_response }}
              />
            </div>
          )}

          {/* Источники */}
          {parsedData.sources && parsedData.sources.length > 0 && (
            <div className="detail-section">
              <h3>
                <Link2 size={20} />
                Источники
                <span className="section-count">{parsedData.sources.length}</span>
              </h3>
              <div className="sources-list">
                {parsedData.sources.map((source, index) => (
                  <div key={index} className={`source-item ${source.used ? 'used' : 'not-used'}`}>
                    <div className="source-header">
                      <span className="source-position">{index + 1}</span>
                      {source.used && <span className="source-badge">Использован</span>}
                    </div>
                    <div className="source-content">
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="source-title"
                      >
                        {source.title || 'Без названия'}
                        <ExternalLink size={14} style={{ marginLeft: '6px' }} />
                      </a>
                      <span className="source-url">{source.url}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Поисковые запросы */}
          {parsedData.searchQueries && parsedData.searchQueries.length > 0 && (
            <div className="detail-section">
              <h3>
                <Search size={20} />
                Связанные поисковые запросы
                <span className="section-count">{parsedData.searchQueries.length}</span>
              </h3>
              <div className="search-queries-list">
                {parsedData.searchQueries.map((query, index) => (
                  <div key={index} className="search-query-item">
                    <Search size={14} />
                    <span>{typeof query === 'string' ? query : query.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Подсказки */}
          {parsedData.hints && parsedData.hints.length > 0 && (
            <div className="detail-section">
              <h3>
                <AlertCircle size={20} />
                Подсказки для пользователей
                <span className="section-count">{parsedData.hints.length}</span>
              </h3>
              <div className="hints-list">
                {parsedData.hints.map((hint, index) => (
                  <div key={index} className="hint-item">
                    {typeof hint === 'string' ? hint : (hint.text || hint.url || JSON.stringify(hint))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Результаты поиска (SERP) */}
          {parsedData.serpItems && parsedData.serpItems.length > 0 && (
            <div className="detail-section">
              <h3>
                <ExternalLink size={20} />
                Результаты поиска (SERP)
                <span className="section-count">{parsedData.serpItems.length}</span>
              </h3>
              <div className="serp-results">
                {parsedData.serpItems.map((item, index) => (
                  <div key={index} className="serp-item">
                    <span className="serp-position">{item.position || index + 1}</span>
                    <div className="serp-content">
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="serp-title"
                      >
                        {item.title}
                        <ExternalLink size={14} style={{ marginLeft: '6px' }} />
                      </a>
                      {item.snippet && (
                        <p className="serp-snippet">{item.snippet}</p>
                      )}
                      <span className="serp-url">{item.url}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onClose}
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

export default History;
